import type { EditableField, SalaryTableActions } from "@shared/views/salary-table/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useRoute } from "wouter";
import { PageHeader } from "@/components/page-header";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { SalaryTableView } from "@/views/salary-table/SalaryTableView";

export default function SalaryTableDetail() {
  const [, params] = useRoute("/salary-tables/:id");
  const id = params?.id;
  const { hasPermission } = useAuth();
  const canManageSalaryTables = hasPermission("salary_tables");
  const { toast } = useToast();

  const { data: table } = useQuery(orpc.salaryTables.getTable.queryOptions({ input: { id: id! } }));

  const { data: bands = [], isLoading } = useQuery(
    orpc.salaryTables.listBands.queryOptions({ input: { id: id! } }),
  );

  const { data: investmentData } = useQuery(orpc.salaryTables.investmentRounds.queryOptions());

  const currentRound = useMemo(
    () => investmentData?.rounds.find((r) => r.section === "current") ?? null,
    [investmentData],
  );
  const spread = currentRound ? currentRound.pricePerShare - (investmentData?.strikePrice ?? 0) : 0;

  const updateTableMutation = useMutation({
    mutationFn: (data: { name?: string; fxRate?: string | null }) =>
      rpcClient.salaryTables.updateTable({ id: id!, ...data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.salaryTables.getTable.queryOptions({ input: { id: id! } }).queryKey,
      });
    },
    onError: () => toast({ title: "Erro ao atualizar tabela", variant: "destructive" }),
  });

  const updateBandMutation = useMutation({
    mutationFn: ({
      bandId,
      field,
      value,
    }: {
      bandId: string;
      field: EditableField;
      value: number | string;
    }) => rpcClient.salaryTables.updateBand({ id: id!, bandId, [field]: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.salaryTables.listBands.queryOptions({ input: { id: id! } }).queryKey,
      });
    },
    onError: () => toast({ title: "Erro ao atualizar faixa", variant: "destructive" }),
  });

  const createBandMutation = useMutation({
    mutationFn: () => {
      const nextPosition = bands.length > 0 ? Math.max(...bands.map((b) => b.position)) + 1 : 1;
      return rpcClient.salaryTables.createBand({
        id: id!,
        key: String(nextPosition),
        minValue: 0,
        midValue: 0,
        maxValue: 0,
        sopMeio: 0,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.salaryTables.listBands.queryOptions({ input: { id: id! } }).queryKey,
      });
    },
    onError: () => toast({ title: "Erro ao adicionar linha", variant: "destructive" }),
  });

  const deleteBandMutation = useMutation({
    mutationFn: (bandId: string) => rpcClient.salaryTables.deleteBand({ id: id!, bandId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.salaryTables.listBands.queryOptions({ input: { id: id! } }).queryKey,
      });
    },
    onError: () => toast({ title: "Erro ao remover faixa", variant: "destructive" }),
  });

  if (!id) return null;

  const actions: SalaryTableActions | undefined = canManageSalaryTables
    ? {
        onUpdateFxRate: async (next) => {
          await updateTableMutation.mutateAsync({ fxRate: String(next) });
        },
        onUpdateBandField: async (input) => {
          await updateBandMutation.mutateAsync(input);
        },
        onAddBand: async () => {
          await createBandMutation.mutateAsync();
        },
        onDeleteBand: async (bandId) => {
          await deleteBandMutation.mutateAsync(bandId);
        },
      }
    : undefined;

  return (
    <div className="flex-1 space-y-6">
      <PageHeader title={table?.name ?? "Tabela Salarial"} backTo="/salary-tables" />
      <SalaryTableView
        data={{
          id,
          name: table?.name ?? "",
          fxRate: table?.fxRate ? parseFloat(table.fxRate) : null,
          spread,
          pricePerShare: currentRound?.pricePerShare ?? null,
          strikePrice: investmentData?.strikePrice ?? null,
          bands: bands.map((b) => ({
            id: b.id,
            key: b.key,
            midValue: b.midValue,
            sopMeio: b.sopMeio,
            allowedAmplitude: b.allowedAmplitude ?? null,
            position: b.position,
          })),
        }}
        actions={actions}
        isLoading={isLoading}
      />
    </div>
  );
}
